import axios from "axios";
import React, { useEffect, useRef, useState } from "react";

import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Bell,
  BriefcaseBusiness,
  Building2,
  Check,
  CheckCircle2,
  ChevronDown,
  Clock3,
  Eye,
  EyeOff,
  FileText,
  Globe2,
  HeartHandshake,
  Home,
  Lightbulb,
  LockKeyhole,
  LogIn,
  LogOut,
  Mail,
  MapPin,
  Menu,
  MessageCircle,
  MessageSquare,
  Phone,
  Search,
  ShieldCheck,
  Smartphone,
  UserCircle,
  UserRound,
  Users,
  X,
} from "lucide-react";
import {
  MapContainer,
  TileLayer,
  useMapEvents,
} from "react-leaflet";
import {
  VIJAYANAGAR_WARDS,
  CIVIC_CATEGORIES,
  CIVIC_CATEGORIES_KN,
  wardSlug,
} from "../wardData";

const BACKEND = process.env.REACT_APP_BACKEND_URL;




const SITE_URL = "https://vijayanagar.org";
const SITE_NAME = "Vijayanagar Civic Portal";

const SEO_DEFAULT = {
  title: "Vijayanagar Civic Portal | Complaints, Civic Services & Wards",
  description:
    "Vijayanagar Civic Portal for residents of Vijayanagar, Bengaluru to report civic complaints, track issues, explore wards, find public services and view local civic information.",
  index: true,
};

const SEO_ROUTES = {
  "/": {
    title: "Vijayanagar Civic Portal | Complaints, Civic Services & Wards",
    description:
      "Report civic complaints in Vijayanagar, Bengaluru, track local issues, explore 13 wards, find public services and view public civic activity.",
    index: true,
  },
  "/about": {
    title: "About Vijayanagar Civic Portal | Bengaluru Civic Information",
    description:
      "Learn about the Vijayanagar Civic Portal and its digital tools for residents to report civic issues, follow complaints and access local civic information.",
    index: true,
  },
  "/author": {
    title: "About Vijayanagar Civic Portal | Vijayanagar Bengaluru",
    description:
      "Learn about the Vijayanagar Civic Portal initiative, its purpose and digital civic services for residents across Vijayanagar, Bengaluru.",
    index: true,
  },
  "/complaints": {
    title: "Vijayanagar Complaints | Report Civic Issues in Bengaluru",
    description:
      "Find information about Vijayanagar civic complaints, report local problems, track complaint progress and explore common civic issues in Bengaluru.",
    index: true,
  },
  "/report-issue": {
    title: "Report a Civic Complaint in Vijayanagar | Bengaluru",
    description:
      "Report potholes, damaged roads, drainage, water supply, garbage, street lights, sanitation and other civic issues in Vijayanagar, Bengaluru.",
    index: true,
  },
  "/track-complaint": {
    title: "Track Vijayanagar Civic Complaint | Complaint Status",
    description:
      "Track the status and timeline of a civic complaint submitted through the Vijayanagar Civic Portal using its complaint tracking ID.",
    index: true,
  },
  "/wards": {
    title: "Vijayanagar Wards Bengaluru | Ward Civic Information & Issues",
    description:
      "Explore Vijayanagar wards in Bengaluru, view ward-level civic activity, reported issues and local public information.",
    index: true,
  },
  "/dashboard": {
    title: "Vijayanagar Civic Dashboard | Bengaluru Civic Issues",
    description:
      "View public civic activity, reported issues, resolution information and civic service statistics for Vijayanagar, Bengaluru.",
    index: true,
  },
  "/services": {
    title: "Vijayanagar Civic Services Bengaluru | Local Public Services",
    description:
      "Find public and civic services in Vijayanagar, Bengaluru including schools, hospitals, police stations, libraries, temples and local resources.",
    index: true,
  },
  "/community": {
    title: "Vijayanagar Community Bengaluru | Local Civic Information",
    description:
      "Explore community information, civic suggestions and local resources for residents of Vijayanagar, Bengaluru.",
    index: true,
  },
  "/volunteer": {
    title: "Vijayanagar Volunteer & Community Service | Bengaluru",
    description:
      "Learn how to participate in community service and civic volunteering activities connected with Vijayanagar residents.",
    index: true,
  },
  "/jobs": {
    title: "Vijayanagar Jobs Bengaluru | Local Job Opportunities",
    description:
      "Browse local job opportunities and employment listings available through the Vijayanagar Civic Portal.",
    index: true,
  },
  "/contact": {
    title: "Contact Vijayanagar Civic Portal | Bengaluru Civic Help",
    description:
      "Contact the Vijayanagar Civic Portal for help with civic complaints, complaint tracking, ward information and public civic services.",
    index: true,
  },
  "/services/temples": {
    title: "Temples in Vijayanagar Bengaluru | Local Temple Information",
    description:
      "Find temples and places of worship in and around Vijayanagar, Bengaluru through the local civic services directory.",
    index: true,
  },
  "/services/hospitals": {
    title: "Hospitals in Vijayanagar Bengaluru | Healthcare Directory",
    description:
      "Find hospitals and healthcare facilities in and around Vijayanagar, Bengaluru through the local civic services directory.",
    index: true,
  },
  "/services/police-stations": {
    title: "Police Stations in Vijayanagar Bengaluru | Public Safety",
    description:
      "Find police stations and public safety information for residents in Vijayanagar, Bengaluru.",
    index: true,
  },
  "/services/libraries": {
    title: "Libraries in Vijayanagar Bengaluru | Local Library Directory",
    description:
      "Find libraries and public learning resources in and around Vijayanagar, Bengaluru.",
    index: true,
  },
  "/services/schools": {
    title: "Schools in Vijayanagar Bengaluru | Local School Directory",
    description:
      "Find schools and education-related public information in and around Vijayanagar, Bengaluru.",
    index: true,
  },
  "/admin": { title: "Admin | Vijayanagar Civic Portal", description: "", index: false },
  "/profile": { title: "Resident Profile | Vijayanagar Civic Portal", description: "", index: false },
  "/reports": { title: "My Reports | Vijayanagar Civic Portal", description: "", index: false },
  "/reset-password": { title: "Reset Password | Vijayanagar Civic Portal", description: "", index: false },
  "/post-job": { title: "Post a Job | Vijayanagar Civic Portal", description: "", index: false },
};

function upsertMeta(attribute, key, content) {
  let element = document.head.querySelector(`meta[${attribute}="${key}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content || "");
}

function upsertLink(rel, href) {
  let element = document.head.querySelector(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", rel);
    document.head.appendChild(element);
  }
  element.setAttribute("href", href);
}

function upsertJsonLd(id, data) {
  let element = document.head.querySelector(`script[data-seo-id="${id}"]`);
  if (!element) {
    element = document.createElement("script");
    element.type = "application/ld+json";
    element.setAttribute("data-seo-id", id);
    document.head.appendChild(element);
  }
  element.textContent = JSON.stringify(data);
}

function getWardSeo(cleanRoute) {
  const match = cleanRoute.match(/^\/ward\/([^/]+)$/);
  if (!match) return null;

  const slug = match[1].toLowerCase();
  const ward = VIJAYANAGAR_WARDS.find((item) => wardSlug(item.name) === slug);
  if (!ward) return null;

  return {
    title: `${ward.name} Ward ${ward.number}, Vijayanagar Bengaluru | Civic Issues`,
    description:
      `View civic information, local issues, complaint activity and public information for ${ward.name} Ward ${ward.number} in Vijayanagar, Bengaluru.`,
    index: true,
    ward,
  };
}

function getBreadcrumbs(cleanRoute, seo) {
  const crumbs = [{ name: "Home", url: `${SITE_URL}/` }];
  if (cleanRoute === "/") return crumbs;

  if (seo.ward) {
    crumbs.push({ name: "Vijayanagar Wards", url: `${SITE_URL}/wards` });
    crumbs.push({ name: seo.ward.name, url: `${SITE_URL}${cleanRoute}` });
    return crumbs;
  }

  const labels = {
    "/complaints": "Vijayanagar Complaints",
    "/report-issue": "Report a Civic Complaint",
    "/track-complaint": "Track Complaint",
    "/wards": "Vijayanagar Wards",
    "/dashboard": "Civic Dashboard",
    "/services": "Civic Services",
    "/community": "Community",
    "/volunteer": "Volunteer",
    "/jobs": "Local Jobs",
    "/contact": "Contact",
  };
  const label = labels[cleanRoute] || seo.title.split("|")[0].trim();
  crumbs.push({ name: label, url: `${SITE_URL}${cleanRoute}` });
  return crumbs;
}

function updateSEO(route) {
  const cleanRoute = route.split("?")[0].split("#")[0] || "/";
  const wardSeo = getWardSeo(cleanRoute);
  const seo = wardSeo || SEO_ROUTES[cleanRoute] || SEO_DEFAULT;
  const canonical = `${SITE_URL}${cleanRoute === "/" ? "/" : cleanRoute}`;
  const robots = seo.index === false
    ? "noindex, nofollow, noarchive"
    : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";

  document.documentElement.lang = "en-IN";
  document.title = seo.title;

  upsertMeta("name", "description", seo.description);
  upsertMeta("name", "robots", robots);
  upsertMeta("name", "googlebot", robots);
  upsertMeta("name", "theme-color", "#0f766e");
  upsertMeta("property", "og:title", seo.title);
  upsertMeta("property", "og:description", seo.description);
  upsertMeta("property", "og:url", canonical);
  upsertMeta("property", "og:type", "website");
  upsertMeta("property", "og:site_name", SITE_NAME);
  upsertMeta("property", "og:locale", "en_IN");
  upsertMeta("name", "twitter:card", "summary");
  upsertMeta("name", "twitter:title", seo.title);
  upsertMeta("name", "twitter:description", seo.description);
  upsertLink("canonical", canonical);

  const breadcrumbs = getBreadcrumbs(cleanRoute, seo);
  upsertJsonLd("organization", {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE_NAME,
    url: SITE_URL,
  });
  upsertJsonLd("webpage", {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: seo.title,
    description: seo.description,
    url: canonical,
    inLanguage: "en-IN",
    isPartOf: { "@type": "WebSite", name: SITE_NAME, url: SITE_URL },
  });
  upsertJsonLd("breadcrumbs", {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbs.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  });

  if (cleanRoute === "/complaints") {
    upsertJsonLd("complaints-faq", {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "How do I report a civic complaint in Vijayanagar?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Use the Report a Civic Complaint option, select the issue category and ward, describe the problem and submit the complaint through the portal.",
          },
        },
        {
          "@type": "Question",
          name: "How can I track my Vijayanagar complaint?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Use the Track a Complaint option and enter the complaint tracking information provided after submission.",
          },
        },
        {
          "@type": "Question",
          name: "What types of local issues can I report?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Supported civic concerns include potholes, roads, drainage, water supply, garbage, street lights, sanitation, parks and other local civic issues.",
          },
        },
      ],
    });
  }
}

const api = axios.create({
  baseURL: `${BACKEND}/api`,
  withCredentials: true,
});

const copy = {
  en: {
    home: "Home",
    report: "Report an Issue",
    track: "Track Complaint",
    wards: "Explore Wards",
    dashboard: "Civic Dashboard",
    services: "Services",
    community: "Community",
    jobs: "Jobs",
    login: "Resident Login",
    myReports: "My Reports",
    profile: "Profile",
    logout: "Logout",
    reportNow: "Report a Civic Problem",
    trackNow: "Track My Complaint",
    exploreWard: "Explore My Ward",
    dashboardBtn: "Civic Dashboard",
    reported: "Issues Reported",
    progress: "In Progress",
    resolved: "Resolved",
    servicesCount: "Civic Services",
    reportTitle: "Report a Civic Issue",
    title: "Issue title",
    category: "Category",
    subCategory: "Sub-category",
    ward: "Ward",
    description: "Describe the problem",
    address: "Address",
    landmark: "Landmark",
    priority: "Priority",
    photo: "Photos",
    location: "Location",
    getLocation: "Use My Location",
    captured: "Location captured",
    submit: "Submit Complaint",
    loginRequired: "Sign in to report an issue",
    success: "Complaint Submitted Successfully",
    trackingId: "Tracking ID",
    status: "Status",
    timeline: "Complaint Timeline",
    noResult: "Complaint not found.",
    feedback: "Citizen Feedback",
    reopen: "Reopen Complaint",
    resolvedQuestion: "Was the issue actually fixed?",
    yes: "Yes",
    partial: "Partially",
    no: "No",
    save: "Save",
    close: "Close",
  },
  kn: {
    home: "ಮುಖ್ಯಪುಟ",
    report: "ಸಮಸ್ಯೆ ವರದಿ ಮಾಡಿ",
    track: "ದೂರು ಟ್ರ್ಯಾಕ್ ಮಾಡಿ",
    wards: "ವಾರ್ಡ್‌ಗಳು",
    dashboard: "ನಾಗರಿಕ ಡ್ಯಾಶ್‌ಬೋರ್ಡ್",
    services: "ಸೇವೆಗಳು",
    community: "ಸಮುದಾಯ",
    jobs: "ಉದ್ಯೋಗಗಳು",
    login: "ನಿವಾಸಿ ಲಾಗಿನ್",
    myReports: "ನನ್ನ ದೂರುಗಳು",
    profile: "ಪ್ರೊಫೈಲ್",
    logout: "ಲಾಗ್‌ಔಟ್",
    reportNow: "ನಾಗರಿಕ ಸಮಸ್ಯೆ ವರದಿ ಮಾಡಿ",
    trackNow: "ನನ್ನ ದೂರು ಟ್ರ್ಯಾಕ್ ಮಾಡಿ",
    exploreWard: "ನನ್ನ ವಾರ್ಡ್ ನೋಡಿ",
    dashboardBtn: "ನಾಗರಿಕ ಡ್ಯಾಶ್‌ಬೋರ್ಡ್",
    reported: "ವರದಿಯಾದ ಸಮಸ್ಯೆಗಳು",
    progress: "ಪ್ರಗತಿಯಲ್ಲಿವೆ",
    resolved: "ಪರಿಹಾರವಾದವು",
    servicesCount: "ನಾಗರಿಕ ಸೇವೆಗಳು",
    reportTitle: "ನಾಗರಿಕ ಸಮಸ್ಯೆ ವರದಿ ಮಾಡಿ",
    title: "ಸಮಸ್ಯೆಯ ಶೀರ್ಷಿಕೆ",
    category: "ವಿಭಾಗ",
    subCategory: "ಉಪ-ವಿಭಾಗ",
    ward: "ವಾರ್ಡ್",
    description: "ಸಮಸ್ಯೆಯನ್ನು ವಿವರಿಸಿ",
    address: "ವಿಳಾಸ",
    landmark: "ಗುರುತು",
    priority: "ಆದ್ಯತೆ",
    photo: "ಫೋಟೋಗಳು",
    location: "ಸ್ಥಳ",
    getLocation: "ನನ್ನ ಸ್ಥಳ ಬಳಸಿ",
    captured: "ಸ್ಥಳ ದಾಖಲಿಸಲಾಗಿದೆ",
    submit: "ದೂರು ಸಲ್ಲಿಸಿ",
    loginRequired: "ದೂರು ಸಲ್ಲಿಸಲು ಮೊದಲು ಲಾಗಿನ್ ಮಾಡಿ.",
    success: "ದೂರು ಯಶಸ್ವಿಯಾಗಿ ಸಲ್ಲಿಸಲಾಗಿದೆ",
    trackingId: "ಟ್ರ್ಯಾಕಿಂಗ್ ID",
    status: "ಸ್ಥಿತಿ",
    timeline: "ದೂರು ಸಮಯರೇಖೆ",
    noResult: "ದೂರು ಕಂಡುಬಂದಿಲ್ಲ.",
    feedback: "ನಾಗರಿಕ ಪ್ರತಿಕ್ರಿಯೆ",
    reopen: "ದೂರು ಮರುತೆರೆಯಿರಿ",
    resolvedQuestion: "ಸಮಸ್ಯೆ ನಿಜವಾಗಿಯೂ ಪರಿಹಾರವಾಗಿದೆಯೇ?",
    yes: "ಹೌದು",
    partial: "ಭಾಗಶಃ",
    no: "ಇಲ್ಲ",
    save: "ಉಳಿಸಿ",
    close: "ಮುಚ್ಚಿ",
  },
};

const subCategories = {
  Roads: ["Pothole", "Damaged Road", "Footpath", "Road Marking"],
  Water: ["No Supply", "Leakage", "Low Pressure", "Contamination"],
  Waste: ["Garbage", "Overflowing Bin", "Illegal Dumping"],
  "Street Lights": ["Not Working", "Dark Area", "Damaged Pole"],
  Drainage: ["Blocked Drain", "Sewage Overflow", "Flooding"],
  Parks: ["Maintenance", "Tree", "Playground"],
  Schools: ["Infrastructure", "Toilet", "Water", "Safety"],
  Health: ["Facility", "Sanitation", "Equipment"],
  Traffic: ["Signal", "Road Safety", "Marking", "Parking"],
  "Animal Issues": ["Stray Animals", "Animal Waste", "Other"],
  Encroachment: ["Public Space", "Footpath", "Road"],
  Sanitation: ["Cleaning", "Public Hygiene", "Other"],
  Other: ["Other"],
};

const wardPath = (ward) => `/ward/${wardSlug(ward.name)}`;


function Gate({ t, onLogin, onBack }) {
  return (
    <div className="civic-gate">
      <LogIn size={42} />
      <h1>{t.login}</h1>
      <button className="civic-btn primary" onClick={onLogin}>
        {t.login}
      </button>
      <button className="civic-btn secondary" onClick={onBack}>
        {t.home}
      </button>
    </div>
  );
}


function SectionHeading({ title, text }) {
  return (
    <div className="section-heading">
      <span className="eyebrow">CIVIC SERVICES</span>
      <h2>{title}</h2>
      <p>{text}</p>
    </div>
  );
}


function CivicStat({ icon, value, label }) {
  return (
    <div className="civic-stat">
      <div className="stat-icon">{icon}</div>
      <strong>{value}</strong>
      <span>{label}</span>
    </div>
  );
}


function MiniAction({ icon, title, onClick }) {
  return (
    <button className="mini-action" onClick={onClick}>
      {icon}
      <span>{title}</span>
      <ArrowRight size={15} />
    </button>
  );
}


function InfoCard({ icon, title, text, button, onClick }) {
  return (
    <article className="info-card">
      <div className="info-card-icon">{icon}</div>
      <h3>{title}</h3>
      <p>{text}</p>
      <button onClick={onClick}>
        {button}
        <ArrowRight size={16} />
      </button>
    </article>
  );
}


function SimplePage({ title, onBack, children }) {
  return (
    <div className="civic-site">
      <header className="civic-header">
        <div className="civic-header-inner">
          <button className="civic-brand brand-button" onClick={onBack}>
            <span className="civic-logo">V</span>
            <span className="civic-brand-text">
              <strong>Vijayanagar</strong>
              <small>Civic Portal</small>
            </span>
          </button>

          <button className="login-btn" onClick={onBack}>
            <Home size={16} />
            Home
          </button>
        </div>
      </header>

      <main className="inner-page">
        <div className="content-width">
          <button className="back-link" onClick={onBack}>
            ← Home
          </button>

          <div className="page-heading">
            <span className="eyebrow">VIJAYANAGAR CIVIC CONNECT</span>
            <h1>{title}</h1>
          </div>

          {children}
        </div>
      </main>
    </div>
  );
}


function LocationPickerMap({ initialCenter, onCenterChange }) {
  const LocationTracker = () => {
    useMapEvents({
      moveend(event) {
        const center = event.target.getCenter();

        onCenterChange([
          center.lat,
          center.lng,
        ]);
      },
    });

    return null;
  };

  return (
    <div className="location-picker-map">
      <MapContainer
        center={initialCenter}
        zoom={17}
        scrollWheelZoom={true}
        dragging={true}
        zoomControl={true}
        className="location-picker-canvas"
      >
        <TileLayer
          attribution='&copy; OpenStreetMap contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        <LocationTracker />
      </MapContainer>

      <div
        className="location-picker-pin"
        aria-hidden="true"
      >
        <div className="location-picker-pin-head">
          <MapPin size={30} />
        </div>
        <div className="location-picker-pin-shadow" />
      </div>

      <div className="location-picker-hint">
        Drag the map to place the pin exactly
      </div>
    </div>
  );
}


function Modal({ type, user, setUser, close, t, standalone = false }) {
  const [locationPickerOpen, setLocationPickerOpen] = useState(false);
  const [locationCenter, setLocationCenter] = useState([
    12.9620,
    77.5390,
  ]);
  const [mode, setMode] = useState(type);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    title: "",
    category: CIVIC_CATEGORIES[0] || "Other",
    sub_category: "",
    ward: "",
    description: "",
    address: "",
    landmark: "",
    priority: "Normal",
    lat: "",
    lng: "",
    photos: [],
    tracking: "",
  });

  const [result, setResult] = useState(null);
  const [trackResult, setTrackResult] = useState(null);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotMessage, setForgotMessage] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const update = (key, value) => {
    setForm((p) => ({ ...p, [key]: value }));
    setError("");
  };

  const openLocationPicker = () => {
    setError("");

    const defaultCenter = [12.9620, 77.5390];

    setLocationCenter(
      form.lat && form.lng
        ? [Number(form.lat), Number(form.lng)]
        : defaultCenter
    );

    setLocationPickerOpen(true);
  };

  const confirmLocation = () => {
    if (
      !Array.isArray(locationCenter) ||
      locationCenter.length !== 2
    ) {
      setError("Please select a location on the map.");
      return;
    }

    update("lat", String(locationCenter[0]));
    update("lng", String(locationCenter[1]));

    setLocationPickerOpen(false);
  };

  const submitAuth = async (e) => {
    e.preventDefault();
    setError("");

    if (mode === "register") {
      if (form.password.length < 8) {
        setError("Password must be at least 8 characters.");
        return;
      }

      if (form.password !== confirmPassword) {
        setError("Passwords do not match.");
        return;
      }

      if (!/^\d{10}$/.test(form.phone)) {
        setError("Please enter a valid 10-digit mobile number.");
        return;
      }
    }

    setSubmitting(true);

    try {
      const endpoint =
        mode === "login" ? "/auth/login" : "/auth/register";

      const payload = {
        email: form.email.trim(),
        password: form.password,
      };

      if (mode === "register") {
        payload.name = form.name.trim();
        payload.phone = form.phone.trim();
      }

      const r = await api.post(endpoint, payload);

      setUser(r.data);

      if (standalone && type === "report") {
        setMode("report");
      } else {
        close();
      }

      setConfirmPassword("");
      setShowPassword(false);
      setShowConfirmPassword(false);
    } catch (err) {
      const detail = err.response?.data?.detail;

      if (typeof detail === "string") {
        setError(detail);
      } else if (Array.isArray(detail)) {
        setError(
          detail
            .map((item) => item?.msg || "Please check your information.")
            .join(" ")
        );
      } else if (detail && typeof detail === "object") {
        setError(detail.msg || "Unable to continue.");
      } else {
        setError("Unable to continue. Please check your details.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const submitForgotPassword = async (e) => {
    e.preventDefault();
    setError("");
    setForgotMessage("");

    if (!forgotEmail.trim()) {
      setError("Please enter your email address.");
      return;
    }

    setSubmitting(true);

    try {
      const r = await api.post("/auth/forgot-password", {
        email: forgotEmail.trim(),
      });

      setForgotMessage(
        r.data?.message ||
          "If an account exists for this email, a password reset link has been sent."
      );
    } catch (err) {
      const detail = err.response?.data?.detail;

      if (typeof detail === "string") {
        setError(detail);
      } else if (Array.isArray(detail)) {
        setError(
          detail
            .map((item) => item?.msg || "Invalid email address.")
            .join(" ")
        );
      } else if (detail && typeof detail === "object") {
        setError(detail.msg || "Unable to send reset link.");
      } else {
        setError("Unable to send reset link. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const submitReport = async (e) => {
    e.preventDefault();

    if (!user) {
      setMode("login");
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        title: form.title,
        category: form.category,
        sub_category: form.sub_category,
        ward: form.ward,
        description: form.description,
        address: form.address,
        landmark: form.landmark,
        priority: form.priority,
      };

      if (form.lat && form.lng) {
        payload.latitude = Number(form.lat);
        payload.longitude = Number(form.lng);
      }

      const r = await api.post("/complaints", payload);

      for (const photo of form.photos || []) {
        const fd = new FormData();
        fd.append("photo", photo);
        await api.post(`/complaints/${r.data.tracking_id}/photo`, fd);
      }

      setResult(r.data);
    } catch (err) {
      console.error("Complaint submission error:", err.response?.data);

      const detail = err.response?.data?.detail;

      let message = "Unable to submit complaint.";

      if (typeof detail === "string") {
        message = detail;
      } else if (Array.isArray(detail)) {
        message = detail
          .map((item) => item?.msg || "Invalid complaint information.")
          .join(" ");
      } else if (detail && typeof detail === "object") {
        message = detail.msg || "Some complaint information is invalid.";
      }

      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const track = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setTrackResult(null);

    try {
      const r = await api.get(`/complaints/${form.tracking.trim()}`);
      setTrackResult(r.data);
    } catch (err) {
      setError(err.response?.status === 404 ? t.noResult : "Unable to find complaint.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className={standalone ? "standalone-modal-shell" : "modal-backdrop"}
      onMouseDown={(e) => !standalone && e.target === e.currentTarget && close()}
    >
      <div className="civic-modal civic-modal-wide">
        <button className="modal-close" onClick={close} aria-label={t.close}>
          <X />
        </button>

        {mode === "login" || mode === "register" ? (
          <div className="resident-auth">

            <div className="resident-auth-header">
              <div className="resident-auth-icon">
                {mode === "login" ? (
                  <LogIn size={24} />
                ) : (
                  <UserRound size={24} />
                )}
              </div>

              <div>
                <span className="eyebrow">RESIDENT ACCESS</span>

                <h2>
                  {mode === "login"
                    ? "Welcome back"
                    : "Create your resident account"}
                </h2>

                <p>
                  {mode === "login"
                    ? "Sign in to manage your civic complaints and profile."
                    : "Register once to report and track civic issues."}
                </p>
              </div>
            </div>

            <form onSubmit={submitAuth} className="resident-auth-form">

              {mode === "register" && (
                <label className="auth-field">
                  <span>Your name</span>

                  <div className="auth-input-wrap">
                    <UserRound size={18} />

                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => update("name", e.target.value)}
                      autoComplete="name"
                      placeholder="Enter your full name"
                      required
                    />
                  </div>
                </label>
              )}

              <label className="auth-field">
                <span>Email address</span>

                <div className="auth-input-wrap">
                  <Mail size={18} />

                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    autoComplete="email"
                    placeholder="you@example.com"
                    required
                  />
                </div>
              </label>

              {mode === "register" && (
                <label className="auth-field">
                  <span>Mobile Number</span>

                  <div className="auth-input-wrap">
                    <Smartphone size={18} />

                    <input
                      type="tel"
                      inputMode="numeric"
                      autoComplete="tel"
                      placeholder="10-digit mobile number"
                      value={form.phone}
                      onChange={(e) =>
                        update(
                          "phone",
                          e.target.value.replace(/\D/g, "").slice(0, 10)
                        )
                      }
                      maxLength="10"
                      required
                    />
                  </div>
                </label>
              )}

              <label className="auth-field">
                <span>Password</span>

                <div className="auth-input-wrap">
                  <LockKeyhole size={18} />

                  <input
                    type={showPassword ? "text" : "password"}
                    value={form.password}
                    onChange={(e) => update("password", e.target.value)}
                    autoComplete={
                      mode === "login"
                        ? "current-password"
                        : "new-password"
                    }
                    placeholder="Enter your password"
                    minLength="8"
                    required
                  />

                  <button
                    type="button"
                    className="auth-password-toggle"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                  >
                    {showPassword ? (
                      <EyeOff size={18} />
                    ) : (
                      <Eye size={18} />
                    )}
                  </button>
                </div>
              </label>

              {mode === "register" && (
                <>
                  <label className="auth-field">
                    <span>Confirm Password</span>

                    <div
                      className={`auth-input-wrap ${
                        confirmPassword &&
                        form.password !== confirmPassword
                          ? "has-error"
                          : confirmPassword &&
                            form.password === confirmPassword
                          ? "has-success"
                          : ""
                      }`}
                    >
                      <LockKeyhole size={18} />

                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) =>
                          setConfirmPassword(e.target.value)
                        }
                        autoComplete="new-password"
                        placeholder="Re-enter your password"
                        minLength="8"
                        required
                      />

                      <button
                        type="button"
                        className="auth-password-toggle"
                        onClick={() =>
                          setShowConfirmPassword((v) => !v)
                        }
                        aria-label={
                          showConfirmPassword
                            ? "Hide password"
                            : "Show password"
                        }
                      >
                        {showConfirmPassword ? (
                          <EyeOff size={18} />
                        ) : (
                          <Eye size={18} />
                        )}
                      </button>
                    </div>
                  </label>

                  <div className="auth-password-rules">
                    <div className={form.password.length >= 8 ? "valid" : ""}>
                      <Check size={14} />
                      At least 8 characters
                    </div>

                    <div
                      className={
                        form.password &&
                        confirmPassword &&
                        form.password === confirmPassword
                          ? "valid"
                          : ""
                      }
                    >
                      <Check size={14} />
                      Passwords match
                    </div>
                  </div>
                </>
              )}

              {mode === "login" && (
                <div className="auth-forgot-row">
                  <button
                    type="button"
                    className="auth-forgot"
                    onClick={() => {
                      setMode("forgot");
                      setError("");
                      setForgotMessage("");
                      setForgotEmail(form.email || "");
                    }}
                  >
                    Forgot Password?
                  </button>
                </div>
              )}

              {error && (
                <div className="auth-error" role="alert">
                  <AlertCircle size={17} />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                className="civic-btn primary full auth-submit"
                disabled={submitting}
              >
                {submitting
                  ? mode === "login"
                    ? "Signing In..."
                    : "Creating Account..."
                  : mode === "login"
                  ? "Sign In"
                  : "Create Resident Account"}
              </button>
            </form>

            <div className="auth-divider">
              <span />
              <small>
                {mode === "login"
                  ? "New resident?"
                  : "Already registered?"}
              </small>
              <span />
            </div>

            <button
              type="button"
              className="auth-switch-button"
              onClick={() => {
                setMode(mode === "login" ? "register" : "login");
                setError("");
                setConfirmPassword("");
                setShowPassword(false);
                setShowConfirmPassword(false);
              }}
            >
              {mode === "login"
                ? "Create a Resident Account"
                : "Sign in to your account"}
            </button>

            <div className="auth-security-note">
              <ShieldCheck size={16} />

              <span>
                Your account information is used for civic-service access
                and communication about your submissions.
              </span>
            </div>

          </div>
        ) : mode === "forgot" ? (
          <>
            <span className="eyebrow">ACCOUNT RECOVERY</span>
            <h2>Forgot Password?</h2>
            <p>
              Enter your registered email address and, if an account exists,
              we will send you a password reset link.
            </p>

            <form onSubmit={submitForgotPassword}>
              <label>
                Email
                <input
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  autoComplete="email"
                  required
                />
              </label>

              {error && <p className="form-error">{error}</p>}

              {forgotMessage && (
                <div className="profile-success">
                  {forgotMessage}
                </div>
              )}

              <button
                className="civic-btn primary full"
                disabled={submitting}
              >
                {submitting ? "Sending..." : "Send Reset Link"}
              </button>
            </form>

            <button
              type="button"
              className="switch-auth"
              onClick={() => {
                setMode("login");
                setError("");
                setForgotMessage("");
              }}
            >
              Back to Resident Login
            </button>
          </>
        ) : mode === "report" ? (
          result ? (
            <div className="success-panel">
              <CheckCircle2 size={46} />
              <span className="eyebrow">COMPLAINT SUBMITTED</span>
              <h2>{t.success}</h2>

              <div className="tracking-box">
                <small>{t.trackingId}</small>
                <strong>{result.tracking_id}</strong>
              </div>

              <p>
                Ward: <strong>{result.ward}</strong>
              </p>
              <p>
                Category: <strong>{result.category}</strong>
              </p>
              <p>
                Status: <strong>{result.status}</strong>
              </p>

              <button className="civic-btn primary" onClick={close}>
                Done
              </button>
            </div>
          ) : !user ? (
            <div className="signin-required-overlay">
              <div className="signin-required-card">

                <button
                  type="button"
                  className="signin-required-close"
                  onClick={close}
                  aria-label="Close"
                  title="Close"
                >
                  <X size={17} strokeWidth={2.2} />
                </button>

                <div className="signin-required-icon">
                  <LogIn size={28} strokeWidth={2.2} />
                </div>

                <h2>Sign in to report an issue</h2>

                <p>
                  Please sign in to your resident account before submitting a
                  civic complaint. This helps us keep your complaint details,
                  updates, and tracking information together.
                </p>

                <div className="signin-required-note">
                  <ShieldCheck size={17} />
                  <span>
                    Your account lets you securely track the status and progress
                    of complaints you submit.
                  </span>
                </div>

                <div className="signin-required-actions">
                  <button
                    type="button"
                    className="signin-required-primary"
                    onClick={() => setMode("login")}
                  >
                    Sign In
                  </button>

                  <button
                    type="button"
                    className="signin-required-secondary"
                    onClick={() => setMode("register")}
                  >
                    Create Account
                  </button>
                </div>

              </div>
            </div>
          ) : (
            <>
              <span className="eyebrow">CIVIC ACTION</span>
              <h2>{t.reportTitle}</h2>

              <form onSubmit={submitReport}>
                <div className="profile-grid">
                  <label>
                    {t.title}
                    <input value={form.title} onChange={(e) => update("title", e.target.value)} required />
                  </label>

                  <label>
                    {t.category}
                    <select
                      value={form.category}
                      onChange={(e) => {
                        update("category", e.target.value);
                        update("sub_category", "");
                      }}
                    >
                      {(CIVIC_CATEGORIES.length ? CIVIC_CATEGORIES : Object.keys(subCategories)).map((c) => (
                        <option key={c}>{c}</option>
                      ))}
                    </select>
                  </label>

                  <label>
                    {t.subCategory}
                    <select
                      value={form.sub_category}
                      onChange={(e) => update("sub_category", e.target.value)}
                    >
                      <option value="">Select sub-category</option>
                      {(subCategories[form.category] || ["Other"]).map((x) => (
                        <option key={x}>{x}</option>
                      ))}
                    </select>
                  </label>

                  <label>
                    {t.ward}
                    <select value={form.ward} onChange={(e) => update("ward", e.target.value)} required>
                      <option value="">Select your ward</option>
                      {VIJAYANAGAR_WARDS.map((w) => (
                        <option value={w.value || w.number} key={w.number}>
                          Ward {w.number} - {w.name}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label>
                    {t.priority}
                    <select value={form.priority} onChange={(e) => update("priority", e.target.value)}>
                      <option>Low</option>
                      <option>Normal</option>
                      <option>High</option>
                      <option>Urgent</option>
                    </select>
                  </label>

                  <label className="profile-full">
                    {t.description}
                    <textarea
                      rows="5"
                      value={form.description}
                      onChange={(e) => update("description", e.target.value)}
                      required
                    />
                  </label>

                  <label className="profile-full">
                    {t.address}
                    <textarea
                      rows="2"
                      value={form.address}
                      onChange={(e) => update("address", e.target.value)}
                    />
                  </label>

                  <label>
                    {t.landmark}
                    <input value={form.landmark} onChange={(e) => update("landmark", e.target.value)} />
                  </label>

                  <div className="profile-location-box">
                    <button
                      type="button"
                      className="civic-btn secondary location-mark-button"
                      onClick={openLocationPicker}
                    >
                      <MapPin size={17} />
                      {form.lat && form.lng
                        ? "Change Exact Location"
                        : "Mark Exact Location"}
                    </button>

                    {form.lat && form.lng && (
                      <div className="location-selected">
                        <span>
                          <MapPin size={15} />
                          Exact location marked
                        </span>
                        <small>
                          {Number(form.lat).toFixed(6)},{" "}
                          {Number(form.lng).toFixed(6)}
                        </small>
                      </div>
                    )}
                  </div>

                  <label className="profile-full">
                    {t.photo}
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      multiple
                      onChange={(e) =>
                        update("photos", Array.from(e.target.files || []))
                      }
                    />
                  </label>
                </div>

                {error && <p className="form-error">{error}</p>}

                <button className="civic-btn primary full" disabled={submitting}>
                  {submitting ? "Submitting..." : t.submit}
                </button>
              </form>

          
          {locationPickerOpen && (
            <div className="location-picker-overlay">
              <div className="location-picker-panel">

                <div className="location-picker-header">
                  <div>
                    <span className="eyebrow">LOCATION</span>
                    <h3>Mark Exact Location</h3>
                    <p>
                      Move the map until the pin is directly over the place
                      where the civic issue is located.
                    </p>
                  </div>

                  <button
                    type="button"
                    className="location-picker-close"
                    onClick={() => setLocationPickerOpen(false)}
                    aria-label="Close location picker"
                  >
                    <X size={20} />
                  </button>
                </div>

                <LocationPickerMap
                  initialCenter={locationCenter}
                  onCenterChange={setLocationCenter}
                />

                <div className="location-picker-coordinates">
                  <MapPin size={16} />
                  <span>
                    {locationCenter[0].toFixed(6)},{" "}
                    {locationCenter[1].toFixed(6)}
                  </span>
                </div>

                <div className="location-picker-actions">
                  <button
                    type="button"
                    className="civic-btn secondary"
                    onClick={() => setLocationPickerOpen(false)}
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    className="civic-btn primary"
                    onClick={confirmLocation}
                  >
                    <MapPin size={17} />
                    Confirm Location
                  </button>
                </div>

              </div>
            </div>
          )}

            </>
          )
        ) : (
          <>
            <span className="eyebrow">COMPLAINT TRACKING</span>
            <h2>{t.track}</h2>

            <form onSubmit={track}>
              <label>
                {t.trackingId}
                <input
                  value={form.tracking}
                  onChange={(e) => update("tracking", e.target.value)}
                  placeholder="VJ-CIVIC-2026-XXXXXX"
                  required
                />
              </label>

              {error && <p className="form-error">{error}</p>}

              <button className="civic-btn primary full" disabled={submitting}>
                {submitting ? "Searching..." : "Track Complaint"}
              </button>
            </form>

            {trackResult && (
              <div className="tracking-result">
                <div className="tracking-box">
                  <small>{t.trackingId}</small>
                  <strong>{trackResult.tracking_id}</strong>
                </div>

                <h3>{trackResult.title}</h3>

                <div className="data-row">
                  <span>Ward</span>
                  <strong>{trackResult.ward}</strong>
                </div>

                <div className="data-row">
                  <span>Category</span>
                  <strong>{trackResult.category}</strong>
                </div>

                <div className="data-row">
                  <span>Status</span>
                  <strong>{trackResult.status}</strong>
                </div>

                <div className="data-row">
                  <span>Priority</span>
                  <strong>{trackResult.priority}</strong>
                </div>

                {trackResult.department && (
                  <div className="data-row">
                    <span>Department</span>
                    <strong>{trackResult.department}</strong>
                  </div>
                )}

                {trackResult.expected_resolution_date && (
                  <div className="data-row">
                    <span>Expected resolution</span>
                    <strong>{trackResult.expected_resolution_date}</strong>
                  </div>
                )}

                <h3>{t.timeline}</h3>

                <div className="timeline-list">
                  {(trackResult.timeline || []).map((event, i) => (
                    <div className="timeline-item" key={i}>
                      <CheckCircle2 size={15} />
                      <div>
                        <strong>{event.status}</strong>
                        <p>{event.note}</p>
                        <small>{event.at}</small>
                      </div>
                    </div>
                  ))}
                </div>

                {trackResult.status === "Resolved" && user && (
                  <FeedbackBox trackingId={trackResult.tracking_id} t={t} />
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}


function FeedbackBox({ trackingId, t }) {
  const [feedback, setFeedback] = useState("");
  const [rating, setRating] = useState(5);
  const [sent, setSent] = useState(false);

  const send = async () => {
    await api.post(`/complaints/${trackingId}/feedback`, {
      feedback,
      rating,
    });
    setSent(true);
  };

  const reopen = async () => {
    await api.post(`/complaints/${trackingId}/reopen`);
    setSent(true);
  };

  if (sent) return <div className="profile-success">Feedback recorded.</div>;

  return (
    <section className="feedback-box">
      <h3>{t.feedback}</h3>
      <p>{t.resolvedQuestion}</p>

      <select value={rating} onChange={(e) => setRating(Number(e.target.value))}>
        <option value="5">5 - Excellent</option>
        <option value="4">4 - Good</option>
        <option value="3">3 - Partial</option>
        <option value="2">2 - Poor</option>
        <option value="1">1 - Not resolved</option>
      </select>

      <textarea
        rows="3"
        value={feedback}
        onChange={(e) => setFeedback(e.target.value)}
        placeholder="Tell us what happened..."
      />

      <div className="hero-buttons">
        <button className="civic-btn primary" onClick={send}>
          Submit Feedback
        </button>
        <button className="civic-btn secondary" onClick={reopen}>
          {t.reopen}
        </button>
      </div>
    </section>
  );
}


function NotFound({ onBack, onReport }) {
  return (
    <div className="not-found-page">
      <div className="not-found-glow" aria-hidden="true" />

      <div className="not-found-card">
        <div className="not-found-code">404</div>

        <div className="not-found-icon" aria-hidden="true">
          <Search size={28} strokeWidth={2} />
        </div>

        <span className="eyebrow">VIJAYANAGAR CIVIC PORTAL</span>

        <h1>Page not found</h1>

        <p>
          The page you are looking for doesn't exist, may have moved,
          or the address may have been entered incorrectly.
        </p>

        <div className="not-found-actions">
          <button
            type="button"
            className="civic-btn primary"
            onClick={onBack}
          >
            <Home size={17} />
            Go to Home
          </button>

          {onReport && (
            <button
              type="button"
              className="civic-btn secondary"
              onClick={onReport}
            >
              <FileText size={17} />
              Report an Issue
            </button>
          )}
        </div>

        <button
          type="button"
          className="not-found-link"
          onClick={() => window.history.back()}
        >
          ← Go back to the previous page
        </button>
      </div>
    </div>
  );
}

export {
  BACKEND, SEO_DEFAULT, SEO_ROUTES, updateSEO, api, copy, subCategories, wardPath,
  VIJAYANAGAR_WARDS, CIVIC_CATEGORIES, CIVIC_CATEGORIES_KN,
  Gate, SectionHeading, CivicStat, MiniAction, InfoCard, SimplePage,
  LocationPickerMap, Modal, FeedbackBox, NotFound,
  AlertCircle, ArrowLeft, ArrowRight, Bell, BriefcaseBusiness, Building2, Check,
  CheckCircle2, ChevronDown, Clock3, Eye, EyeOff, FileText, Globe2, HeartHandshake,
  Home, Lightbulb, LockKeyhole, LogIn, LogOut, Mail, MapPin, Menu, MessageCircle,
  MessageSquare, Phone, Search, ShieldCheck, Smartphone, UserCircle, UserRound, Users, X,
  MapContainer, TileLayer, useMapEvents
};
